import React, { useState, useRef, useEffect } from "react";
import { DateRange } from "react-date-range";
import { addDays, addMonths, format } from "date-fns";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { submitPreApproval } from "../../../services/resident_api";
import { fetchCompaniesAPI, addNewCompanyAPI } from "../../../services/api";
import addIcon from "../../../assets/images/add.png";
import cancelIcon from "../../../assets/images/cancel.png";
import defaultCompanyIcon from "../../../assets/images/parcel1.png";
import { useLockBodyScroll } from "../../../hooks/useLockBodyScroll";

export default function AddParcelModal({
  isOpen,
  onClose,
  onSubmit,
  residentId,
  residentToken,
  residentName,
  residentPhone,
}) {
  // OTP constants
  const OTP_MAX = 6;
  const OTP_MIN = 4;

  // === State ===
  const [allCompanies, setAllCompanies] = useState([]);
  const [filter, setFilter] = useState("");
  const [company, setCompany] = useState("");
  const [companyImgUrl, setCompanyImgUrl] = useState("");
  const [parcelType, setParcelType] = useState("");
  const [parcels, setParcels] = useState("");
  const [range, setRange] = useState([
    {
      startDate: new Date(),
      endDate: addDays(new Date(), 6),
      key: "selection",
    },
  ]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showCal, setShowCal] = useState(false);
  const [apiError, setApiError] = useState("");
  const [screenshots, setScreenshots] = useState([]);
  const [validationError, setValidationError] = useState("");

  // New state for delivery type
  const [deliveryType, setDeliveryType] = useState("normal");

  // COMMENTED OUT: New state for add brand modal
  // const [showAddBrandModal, setShowAddBrandModal] = useState(false);
  // const [newBrandName, setNewBrandName] = useState("");
  // const [newBrandLogo, setNewBrandLogo] = useState(null);
  // const [newBrandLogoPreview, setNewBrandLogoPreview] = useState(null);
  // const [isAddingBrand, setIsAddingBrand] = useState(false);
  // const [brandAddError, setBrandAddError] = useState("");
  // const [brandAddSuccess, setBrandAddSuccess] = useState("");

  // OTP digit boxes
  const [otpDigits, setOtpDigits] = useState(Array(OTP_MAX).fill(""));
  const [otpError, setOtpError] = useState("");
  const inputRefs = useRef([]);

  // Refs
  const fileRef = useRef(null);
  // COMMENTED OUT: const brandLogoRef = useRef(null);
  const modalRef = useRef(null);
  const ddRef = useRef(null);
  const datePickerRef = useRef(null);

  // Track loaded images
  const loadedImages = useRef(new Set());

  useLockBodyScroll(isOpen);

  // === Effects ===
  // Click outside company dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ddRef.current && !ddRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Click outside date picker
  useEffect(() => {
    const handleClickOutsideCal = (e) => {
      if (datePickerRef.current && !datePickerRef.current.contains(e.target)) {
        setShowCal(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutsideCal);
    return () =>
      document.removeEventListener("mousedown", handleClickOutsideCal);
  }, []);

  // Clear API error when inputs change
  // useEffect(() => {
  //   if (apiError) setApiError("");
  // }, [company, parcelType, range, deliveryType, apiError]);

  // Fetch companies when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchCompanies();
    }
  }, [isOpen]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Reset all form fields
      setCompany("");
      setCompanyImgUrl("");
      setFilter("");
      setParcelType("");
      setParcels("");
      setDeliveryType("normal");
      setRange([
        {
          startDate: new Date(),
          endDate: addDays(new Date(), 6),
          key: "selection",
        },
      ]);
      setScreenshots([]);
      setOtpDigits(Array(OTP_MAX).fill(""));
      setOtpError("");
      setApiError("");
      // COMMENTED OUT: setBrandAddSuccess("");
    }
  }, [isOpen]);

  // Helper function to deduplicate companies
  const deduplicateCompanies = (companies) => {
    const seen = new Map();
    const result = [];

    // Process in reverse to keep the first occurrence (from primary table)
    for (let i = companies.length - 1; i >= 0; i--) {
      const company = companies[i];
      const normalizedName = company.company_name.toLowerCase();

      // If we haven't seen this company name before, add it
      if (!seen.has(normalizedName)) {
        seen.set(normalizedName, true);
        result.unshift(company); // Add to beginning to maintain order
      }
    }

    return result;
  };

  const fetchCompanies = async () => {
    try {
      console.log("🔄 Fetching companies from API...");
      const comps = await fetchCompaniesAPI();

      // Map the API response with unique composite IDs
      const mappedCompanies = comps.map((company) => ({
        // Create unique ID by combining source + id
        id: `${company.source}_${company.id}`,
        originalId: company.id,
        company_name: company.company_name,
        company_img: company.company_image,
        source: company.source,
      }));

      console.log("✅ Raw companies fetched:", mappedCompanies.length);

      // Deduplicate companies by name
      const deduplicated = deduplicateCompanies(mappedCompanies);

      console.log("✅ Deduplicated companies:", deduplicated.length);
      console.log("Sample company:", deduplicated[0]);

      setAllCompanies(deduplicated);
    } catch (err) {
      console.error("❌ Error fetching companies", err);
      setAllCompanies([]);
    }
  };

  // Helper function to get company logo URL
  const getCompanyLogoUrl = (company) => {
    if (!company) return defaultCompanyIcon;

    // If company has a valid logo URL, use it
    if (company.company_img && company.company_img.trim() !== "") {
      // Ensure it's a valid URL (not blob)
      if (company.company_img.startsWith("blob:")) {
        return defaultCompanyIcon;
      }
      return company.company_img;
    }

    // Otherwise use default icon
    return defaultCompanyIcon;
  };

  // === Memoized filtered list ===
  const options = React.useMemo(() => {
    const q = filter.toLowerCase();
    return allCompanies.filter((c) => c.company_name.toLowerCase().includes(q));
  }, [filter, allCompanies]);

  // Should show "Add New" option?
  // COMMENTED OUT: This will now always be false since we're disabling the add new feature
  const showAddNewOption = false; // React.useMemo(() => {
  //   return (
  //     filter.trim() !== "" &&
  //     options.length === 0 &&
  //     !allCompanies.some(
  //       (c) => c.company_name.toLowerCase() === filter.toLowerCase()
  //     )
  //   );
  // }, [filter, options, allCompanies]);

  // Check if there are any oversized files in screenshots
  const hasOversizedFiles = React.useMemo(() => {
    const MAX_FILE_SIZE = 5 * 1024 * 1024;
    return screenshots.some(
      (screenshot) => screenshot.file.size > MAX_FILE_SIZE,
    );
  }, [screenshots]);

  // Check if there's an overlapping pre-approval error
  const hasOverlappingError = React.useMemo(() => {
    console.log("🔍 Checking hasOverlappingError. apiError:", apiError);
    if (!apiError) return false;

    return (
      apiError.includes("already exists") ||
      apiError.includes("Overlapping pre-approval") ||
      apiError.includes("A pre-approval for this company")
    );
  }, [apiError]);

  // === Handlers ===
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const MAX_FILE_SIZE = 5 * 1024 * 1024;
    const newPreviews = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      isOversized: file.size > MAX_FILE_SIZE,
    }));

    setScreenshots((p) => [...p, ...newPreviews]);
    setValidationError("");

    if (fileRef.current) {
      fileRef.current.value = "";
    }
  };

  // COMMENTED OUT: Brand logo upload handler
  // const handleBrandLogoUpload = (e) => {
  //   const file = e.target.files[0];
  //   if (!file) return;

  //   setNewBrandLogo(file);
  //   setNewBrandLogoPreview(URL.createObjectURL(file));
  //   setBrandAddError("");
  // };

  function handleInputChange(idx, val) {
    if (!/^\d?$/.test(val)) return;
    const next = [...otpDigits];
    next[idx] = val;
    setOtpDigits(next);
    setOtpError("");
    if (val && idx < OTP_MAX - 1) {
      inputRefs.current[idx + 1]?.focus();
    }
  }

  function handleKeyDown(idx, e) {
    if (e.key === "Backspace" && !otpDigits[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
    if (e.key === "Tab" && idx === OTP_MAX - 1) {
      const code = otpDigits.join("").trim();
      if (code.length > 0 && code.length < OTP_MIN) {
        setOtpError(`Enter a valid ${OTP_MIN}–${OTP_MAX} digit code`);
      }
    }
  }

  // COMMENTED OUT: Add new brand handler
  // const handleAddNewBrand = () => {
  //   setNewBrandName(filter);
  //   setBrandAddError("");
  //   setShowAddBrandModal(true);
  //   setShowDropdown(false);
  // };

  const handleRemoveScreenshot = (index) => {
    const screenshotToRemove = screenshots[index];

    setScreenshots((p) => p.filter((_, j) => j !== index));

    if (screenshotToRemove.preview) {
      URL.revokeObjectURL(screenshotToRemove.preview);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(2) + " MB";
  };

  // COMMENTED OUT: Submit new brand handler
  // const handleSubmitNewBrand = async () => {
  //   if (!newBrandName.trim()) {
  //     setBrandAddError("Please enter a brand name");
  //     return;
  //   }

  //   setIsAddingBrand(true);
  //   setBrandAddError("");
  //   setBrandAddSuccess("");

  //   try {
  //     const formData = new FormData();
  //     formData.append("company_name", newBrandName.trim());

  //     if (newBrandLogo) {
  //       formData.append("company_image", newBrandLogo);
  //     }

  //     console.log("🔄 Calling addNewCompanyAPI with:", {
  //       company_name: newBrandName.trim(),
  //       hasLogo: !!newBrandLogo,
  //     });

  //     const response = await addNewCompanyAPI(formData);
  //     console.log("🔍 Full API response:", response);

  //     if (response.success || response.id) {
  //       let logoUrl = "";

  //       // Extract logo URL from response - check all possible locations
  //       const possiblePaths = [
  //         "company_img_url",
  //         "company_image_url",
  //         "data.company_img_url",
  //         "data.company_image_url",
  //         "data.resident_data.company_img_url",
  //         "image_url",
  //         "data.image_url",
  //         "logo_url",
  //         "data.logo_url",
  //         "url",
  //         "data.url",
  //       ];

  //       for (const path of possiblePaths) {
  //         const value = path
  //           .split(".")
  //           .reduce((obj, key) => obj && obj[key], response);
  //         if (value) {
  //           logoUrl = value;
  //           console.log(`Found logo URL at ${path}:`, logoUrl);
  //           break;
  //         }
  //       }

  //       // If no logo URL found, use null
  //       if (!logoUrl || logoUrl.trim() === "") {
  //         logoUrl = null;
  //       }

  //       console.log("📸 Final logo URL:", logoUrl);

  //       // Create the new company object with UNIQUE ID
  //       const newCompany = {
  //         // Create unique ID using source + id
  //         id: response.id
  //           ? `resident_request_${response.id}`
  //           : `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
  //         originalId: response.id,
  //         company_name: newBrandName.trim(),
  //         company_img: logoUrl,
  //         source: "resident_request",
  //       };

  //       console.log("📝 New company to add:", newCompany);

  //       // Add to local state - check for duplicates by name
  //       setAllCompanies((prev) => {
  //         // Check if company with same name already exists (case insensitive)
  //         const normalizedName = newBrandName.trim().toLowerCase();
  //         const existingIndex = prev.findIndex(
  //           (c) => c.company_name.toLowerCase() === normalizedName
  //         );

  //         if (existingIndex >= 0) {
  //           console.log("⚠️ Company already exists, replacing...");
  //           const updated = [...prev];
  //           updated[existingIndex] = newCompany;
  //           return updated;
  //         }

  //         return [...prev, newCompany];
  //       });

  //       // Set the company as selected
  //       setCompany(newBrandName.trim());
  //       setCompanyImgUrl(logoUrl);
  //       setFilter(newBrandName.trim());

  //       // Show success message
  //       setBrandAddSuccess(
  //         `Brand "${newBrandName.trim()}" added successfully!`
  //       );

  //       // Close add brand modal after short delay
  //       setTimeout(() => {
  //         setShowAddBrandModal(false);
  //         setNewBrandName("");
  //         setNewBrandLogo(null);
  //         setNewBrandLogoPreview(null);

  //         // Clean up blob URL if created
  //         if (newBrandLogoPreview) {
  //           URL.revokeObjectURL(newBrandLogoPreview);
  //         }

  //         // Auto-hide success message
  //         setTimeout(() => {
  //           setBrandAddSuccess("");
  //         }, 3000);
  //       }, 1000);

  //       // Refresh companies list from API to get the latest data
  //       setTimeout(async () => {
  //         await fetchCompanies();
  //       }, 300);

  //       console.log(`🎉 Brand "${newBrandName.trim()}" added successfully!`);
  //     } else {
  //       setBrandAddError(
  //         response?.message || "Failed to add brand. Please try again."
  //       );
  //     }
  //   } catch (error) {
  //     console.error("❌ Error adding brand:", error);

  //     if (error.response) {
  //       console.error("Response data:", error.response.data);
  //       console.error("Response status:", error.response.status);
  //     }

  //     setBrandAddError(
  //       error.response?.data?.message ||
  //         error.message ||
  //         "Failed to add brand. Please try again."
  //     );
  //   } finally {
  //     setIsAddingBrand(false);
  //   }
  // };

  const handleSubmit = async () => {
    // For Open Box: Just close the modal, no API call
    if (deliveryType === "openBox") {
      onClose();
      return;
    }

    // For Normal Delivery: Keep existing validations
    if (!company || !parcelType || !parcels) {
      setApiError(
        "Please select a company, parcel type and number of parcels.",
      );
      return;
    }

    // Check if there are any oversized files
    if (hasOversizedFiles) {
      setApiError("Please remove files larger than 5MB before submitting.");
      return;
    }

    // collect OTP from boxes
    const enteredOtp = otpDigits.filter((d) => d !== "").join("");

    // If OTP is entered, validate it's 4 or 6 digits
    if (enteredOtp && enteredOtp.length !== 4 && enteredOtp.length !== 6) {
      setOtpError(`OTP must be 4 or 6 digits if entered`);
      return;
    }

    // Handle company logo URL
    let sanitizedCompanyImgUrl = companyImgUrl;

    // If no logo URL is set, try to get it from the selected company
    if (!sanitizedCompanyImgUrl || sanitizedCompanyImgUrl.trim() === "") {
      const selectedCompany = allCompanies.find(
        (c) => c.company_name === company,
      );
      if (selectedCompany && selectedCompany.company_img) {
        sanitizedCompanyImgUrl = selectedCompany.company_img;
      } else {
        // Use empty string - backend might have a default
        sanitizedCompanyImgUrl = "";
      }
    }

    // Check if it's a blob URL (invalid for backend)
    if (sanitizedCompanyImgUrl && sanitizedCompanyImgUrl.startsWith("blob:")) {
      sanitizedCompanyImgUrl = "";
    }

    // Check if it's null (from API response)
    if (sanitizedCompanyImgUrl === null) {
      sanitizedCompanyImgUrl = "";
    }

    // Only include if it's a valid URL or empty
    if (
      sanitizedCompanyImgUrl &&
      !sanitizedCompanyImgUrl.startsWith("http") &&
      !sanitizedCompanyImgUrl.startsWith("/")
    ) {
      // If it's not a valid URL, leave it empty
      sanitizedCompanyImgUrl = "";
    }

    console.log("📤 Final company logo URL to send:", sanitizedCompanyImgUrl);

    // prepare form data
    const formData = new FormData();
    formData.append("resident_id", residentId);
    formData.append("name", residentName);
    formData.append("phone_number", residentPhone);
    formData.append("company_name", company);
    formData.append("company_img_url", sanitizedCompanyImgUrl || "");
    formData.append("delivery_type", deliveryType);
    formData.append("parcelType", parcelType);
    formData.append("parcels", parcels);
    formData.append("start_date", format(range[0].startDate, "yyyy-MM-dd"));
    formData.append("end_date", format(range[0].endDate, "yyyy-MM-dd"));

    // Add OTP if entered
    if (enteredOtp) {
      formData.append("text_input_resident_otp_value", enteredOtp);
    } else {
      formData.append("text_input_resident_otp_value", "None");
    }

    screenshots.forEach((entry) => {
      formData.append("screenshots", entry.file);
    });

    console.log("📋 Submitting pre-approval...");

    try {
      const result = await submitPreApproval(formData);

      if (result.success) {
        console.log("✅ Pre-approval submitted successfully!");

        // Clear form
        setOtpDigits(Array(OTP_MAX).fill(""));
        setCompany("");
        setCompanyImgUrl("");
        setFilter("");
        setParcelType("");
        setParcels("");
        setDeliveryType("normal");
        setRange([
          {
            startDate: new Date(),
            endDate: addDays(new Date(), 6),
            key: "selection",
          },
        ]);
        setScreenshots([]);
        setOtpError("");
        setApiError("");

        // Call onSubmit with success
        onSubmit(result.data || {});
        onClose();
      } else {
        console.log("🔍 API returned success=false:", result);

        // Check if it's an overlapping error from the result
        if (result?.message?.includes("Overlapping pre-approval")) {
          setApiError(
            "A pre-approval for this company already exists in the selected date range.",
          );
        } else {
          setApiError(result?.message || "Something went wrong.");
        }
      }
    } catch (error) {
      console.error("Pre-Approval Failed:", error);

      // Check if it's an axios error with response data
      if (error.response?.data) {
        const errorData = error.response.data;
        console.log("🔍 Error response data:", errorData);

        // FIRST check for overlapping pre-approval error
        if (
          errorData.message?.includes("Overlapping pre-approval") ||
          errorData.error_details?.includes("already exists") ||
          errorData.detail?.includes("Overlapping pre-approval") ||
          (typeof errorData === "string" &&
            errorData.includes("Overlapping pre-approval")) ||
          errorData.error?.includes("Overlapping pre-approval")
        ) {
          setApiError(
            "A pre-approval for this company already exists in the selected date range.",
          );
        }
        // Handle company_img_url validation error
        else if (errorData.error_details?.company_img_url) {
          setApiError(
            "Invalid company logo URL. The brand logo couldn't be processed. Please try selecting the brand again or contact support.",
          );
        }
        // Use the message from error response if available
        else if (errorData.message) {
          setApiError(errorData.message);
        }
        // Fallback to default message
        else {
          setApiError("Something went wrong. Please try again.");
        }
      } else {
        // If no response data, use the error message
        setApiError(error.message || "Something went wrong. Please try again.");
      }
    }
  };

  // COMMENTED OUT: Close add brand modal handler
  // const handleCloseAddBrandModal = () => {
  //   setShowAddBrandModal(false);
  //   setNewBrandName("");
  //   setNewBrandLogo(null);
  //   setNewBrandLogoPreview(null);
  //   setBrandAddError("");
  //   setBrandAddSuccess("");
  // };

  // Image error handler
  const handleImageError = (e, companyName) => {
    if (!loadedImages.current.has(e.target.src)) {
      loadedImages.current.add(e.target.src);
      e.target.src = defaultCompanyIcon;
    }
  };

  // Debug: Log companies when they change
  useEffect(() => {
    console.log("📊 Current companies in state:", allCompanies.length);
    if (allCompanies.length > 0) {
      console.log("Companies list sample:", allCompanies.slice(0, 3));
    }
  }, [allCompanies]);

  if (!isOpen) return null;

  return (
    <>
      {/* Main Add Parcel Modal */}
      <div
        className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-start justify-center overflow-y-auto pt-24 sm:pt-16 pb-8"
        onClick={onClose}
      >
        <div
          ref={modalRef}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-xl shadow-lg w-full max-w-xs sm:max-w-md mx-4 z-50"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h2 className="text-lg font-semibold">Add Parcel Details</h2>
            <img
              src={cancelIcon}
              alt="Cancel"
              className="w-6 h-6 cursor-pointer"
              onClick={onClose}
            />
          </div>

          {/* Body */}
          <div className="px-4 py-2 space-y-2">
            {/* Delivery Type Selection */}
            <div className="space-y-3">
              <span className="text-sm font-medium">Choose Delivery Type</span>

              <div className="flex flex-col sm:flex-row gap-3">
                {/* Normal Delivery Card */}
                <div
                  className={`flex-1 p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 relative ${
                    deliveryType === "normal"
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                  onClick={() => setDeliveryType("normal")}
                >
                  <div className="absolute -top-2 right-3 z-20">
                    <span
                      className={`text-xs px-3 py-1 rounded-lg font-semibold ${
                        deliveryType === "normal"
                          ? "bg-blue-500 text-white"
                          : "bg-gray-300 text-gray-700"
                      }`}
                    >
                      📦 Pre-Approved
                    </span>
                  </div>

                  <div className="flex flex-col pt-2">
                    <div className="flex items-start gap-2">
                      <div
                        className={`w-5 h-5 rounded-full border flex items-center justify-center mt-0.5 ${
                          deliveryType === "normal"
                            ? "border-blue-500 bg-blue-500"
                            : "border-gray-300"
                        }`}
                      >
                        {deliveryType === "normal" && (
                          <div className="w-2 h-2 rounded-full bg-white"></div>
                        )}
                      </div>

                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-base font-medium">
                            Normal Delivery
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="-ml-1 mt-2 text-xs text-gray-600 space-y-1">
                      <div>• We'll securely store your parcel</div>
                      <div>• Collect anytime from smart locker</div>
                      <div>• No need to be present</div>
                    </div>
                  </div>
                </div>

                {/* Open Box Delivery Card */}
                <div
                  className={`flex-1 p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 relative ${
                    deliveryType === "openBox"
                      ? "border-green-500 bg-green-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                  onClick={() => setDeliveryType("openBox")}
                >
                  <div className="absolute -top-2 right-3 z-20">
                    <span
                      className={`text-xs px-3 py-1 rounded-lg font-semibold  ${
                        deliveryType === "openBox"
                          ? "bg-green-600 text-white"
                          : "bg-gray-300 text-gray-700"
                      } `}
                    >
                      🤝 Personal Attendance
                    </span>
                  </div>

                  <div className="flex flex-col pt-2">
                    <div className="flex items-start gap-2">
                      <div
                        className={`w-5 h-5 rounded-full border flex items-center justify-center mt-0.5 ${
                          deliveryType === "openBox"
                            ? "border-green-500 bg-green-500"
                            : "border-gray-300"
                        }`}
                      >
                        {deliveryType === "openBox" && (
                          <div className="w-2 h-2 rounded-full bg-white"></div>
                        )}
                      </div>

                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-base font-medium">
                            Open Box Delivery
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="-ml-1 mt-2 text-xs text-gray-600 space-y-1">
                      <div>• You need to personally receive</div>
                      <div>• Inspect contents at delivery time</div>
                      <div>• Sign confirmation receipt</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Open Box Information Panel */}
              {deliveryType === "openBox" && (
                <div className="mt-2 p-3 bg-grey-50 border border-grey-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <span className="text-green-600 text-sm mt-0.5">✅</span>
                    <div>
                      <p className="text-base font-medium text-green-800">
                        Great! For Open Box Delivery:
                      </p>
                      <ul className="text-base text-green-700 mt-1 space-y-0.5">
                        <li>• You'll be notified when delivery arrives</li>
                        <li>• Please be available during selected dates</li>
                        <li>• You'll personally inspect and sign</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Company + OTP - Only for normal delivery */}
            {deliveryType === "normal" && (
              <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-4 sm:space-y-0">
                {/* Company column */}
                <div className="w-full sm:w-full flex flex-col">
                  <span className="text-sm font-medium mb-1">Company Name</span>
                  <div ref={ddRef} className="relative">
                    <input
                      type="text"
                      placeholder="Search brand"
                      value={filter}
                      onChange={(e) => {
                        setFilter(e.target.value);
                        setShowDropdown(true);
                      }}
                      onFocus={() => setShowDropdown(true)}
                      className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                    {showDropdown && (
                      <ul className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg max-h-60 overflow-y-auto shadow-sm">
                        {options.length > 0 ? (
                          options.map((c) => (
                            <li
                              key={c.id}
                              onClick={() => {
                                console.log("Selected company:", c);
                                setCompany(c.company_name);
                                setCompanyImgUrl(c.company_img);
                                setFilter(c.company_name);
                                setShowDropdown(false);
                              }}
                              className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm flex items-center gap-2"
                            >
                              <img
                                src={getCompanyLogoUrl(c)}
                                alt={c.company_name}
                                className="w-5 h-5 object-cover rounded-sm"
                                onError={(e) =>
                                  handleImageError(e, c.company_name)
                                }
                              />
                              <span>{c.company_name}</span>
                              {c.source === "resident_request" && (
                                <span className="text-xs text-gray-400 ml-auto">
                                  (Requested)
                                </span>
                              )}
                            </li>
                          ))
                        ) : filter.trim() !== "" ? (
                          <li className="px-3 py-2 text-sm text-gray-500 text-center">
                            No companies found
                          </li>
                        ) : null}

                        {/* COMMENTED OUT: "Add New Brand" option */}
                        {/* {showAddNewOption && (
                          <>
                            <div className="border-t border-gray-200 my-1"></div>
                            <li
                              onClick={handleAddNewBrand}
                              className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm flex items-center gap-2 text-blue-600"
                            >
                              <div className="w-5 h-5 flex items-center justify-center bg-blue-100 rounded">
                                <span className="text-blue-600 font-bold text-sm">
                                  +
                                </span>
                              </div>
                              <span className="font-medium">
                                Add "{filter}" as new brand
                              </span>
                            </li>
                          </>
                        )} */}
                      </ul>
                    )}
                  </div>
                </div>

                {/* OTP column - Only for normal delivery */}
                <div className="w-full sm:w-auto mt-2 sm:mt-0">
                  <span className="text-sm font-medium">
                    Enter OTP (optional)
                  </span>
                  <div className="flex flex-col items-start">
                    <div className="flex space-x-2 justify-start">
                      {otpDigits.map((digit, i) => (
                        <input
                          key={i}
                          ref={(el) => (inputRefs.current[i] = el)}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={digit}
                          onChange={(e) => handleInputChange(i, e.target.value)}
                          onKeyDown={(e) => handleKeyDown(i, e)}
                          className="w-8 h-10 border border-gray-300 rounded text-center text-lg"
                        />
                      ))}
                    </div>
                    {otpError && (
                      <p className="text-red-500 text-xs mt-1 h-4">
                        {otpError}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Parcel Type - Only for normal delivery */}
            {deliveryType === "normal" && (
              <div>
                <span className="text-sm font-medium">
                  How many parcels are you expecting?
                </span>
                <div className="flex space-x-2 mt-2">
                  <button
                    onClick={() => {
                      setParcelType("One");
                      setParcels("1");
                    }}
                    className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium border ${
                      parcelType === "One"
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 text-gray-700"
                    }`}
                  >
                    One
                  </button>
                  <button
                    onClick={() => {
                      setParcelType("Many");
                      setParcels("2");
                    }}
                    className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium border ${
                      parcelType === "Many"
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 text-gray-700"
                    }`}
                  >
                    Many
                  </button>
                </div>
              </div>
            )}

            {/* Date Range - Only for normal delivery */}
            {deliveryType === "normal" && (
              <div className="space-y-1 relative">
                <span className="text-sm font-medium">Pre Approved Entry</span>
                <div className="flex space-x-2">
                  <button
                    disabled
                    className="flex-1 text-left px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700"
                  >
                    {format(range[0].startDate, "dd MMM")} –{" "}
                    {format(range[0].endDate, "dd MMM")}
                  </button>
                  <button
                    onClick={() => setShowCal(true)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Select Date
                  </button>
                </div>
                {showCal && (
                  <div className="relative" ref={datePickerRef}>
                    <div className="absolute bottom-full mb-2 left-0 right-0 z-50 sm:px-4 px-0">
                      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
                        <div className="flex justify-between items-center mb-2">
                          <button
                            onClick={() =>
                              setRange([
                                {
                                  startDate: new Date(),
                                  endDate: new Date(),
                                  key: "selection",
                                },
                              ])
                            }
                            className="text-red-500 text-sm"
                          >
                            Clear Filter
                          </button>
                          <button
                            onClick={() => setShowCal(false)}
                            className="bg-blue-500 text-white px-4 py-1 rounded text-sm font-medium"
                          >
                            Submit Date
                          </button>
                        </div>
                        <DateRange
                          editableDateInputs
                          moveRangeOnFirstSelection={false}
                          ranges={range}
                          onChange={({ selection }) =>
                            setRange([{ ...selection, key: "selection" }])
                          }
                          months={1}
                          direction="horizontal"
                          minDate={new Date()}
                          maxDate={addMonths(new Date(), 3)}
                          showMonthAndYearPickers={false}
                          className="!w-full"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Overlapping pre-approval error message - MOVED INSIDE the deliveryType condition */}
                {
                  hasOverlappingError
                  // <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                  //   <div className="flex items-start gap-2">
                  //     <span className="text-red-600 text-sm mt-0.5">⚠️</span>
                  //     <div>
                  //       <p className="text-sm font-medium text-red-800">
                  //         Pre-Approval Conflict
                  //       </p>
                  //       <p className="text-sm text-red-700 mt-0.5">
                  //         {apiError}
                  //       </p>
                  //     </div>
                  //   </div>
                  // </div>
                }
              </div>
            )}

            {/* Screenshot Upload - Only for normal delivery */}
            {deliveryType === "normal" && (
              <div className="space-y-2">
                <div className="flex items-start justify-between">
                  <div>
                    <span className="text-base font-medium">
                      Add Screenshot
                    </span>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Image size should be below 5MB
                    </p>
                  </div>
                  <button
                    onClick={() => fileRef.current.click()}
                    className="relative w-8 h-8 flex items-center justify-center border border-gray-300 rounded-full hover:bg-gray-50 mt-1 "
                  >
                    <img src={addIcon} alt="Add" className="w-8 h-8" />
                    {screenshots.length > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 bg-blue-600 text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center">
                        {screenshots.length}
                      </span>
                    )}
                  </button>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    ref={fileRef}
                    className="hidden"
                    onChange={handleFileUpload}
                  />
                </div>

                {/* {hasOversizedFiles && (
                  <div className="p-2 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-600 text-sm">
                      Some images exceed 5MB limit. Please remove them to
                      submit.
                    </p>
                  </div>
                )} */}

                {screenshots.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs text-gray-500 mb-1">
                      Preview ({screenshots.length} image
                      {screenshots.length !== 1 ? "s" : ""})
                    </p>
                    <div className="flex space-x-2 overflow-x-auto pb-2">
                      {screenshots.map(({ preview, file, isOversized }, i) => (
                        <div key={i} className="relative flex-shrink-0 w-24">
                          <div
                            className={`relative ${
                              isOversized
                                ? "border-2 border-red-500"
                                : "border border-gray-300"
                            } rounded-lg overflow-hidden`}
                          >
                            <img
                              src={preview}
                              alt={`Screenshot ${i + 1}`}
                              className="w-full h-16 object-cover"
                            />
                            {isOversized && (
                              <div className="absolute inset-0 bg-red-500 bg-opacity-10 flex items-center justify-center">
                                <span className="text-red-600 text-xs font-bold bg-white bg-opacity-90 px-1 py-0.5 rounded">
                                  OVER 5MB
                                </span>
                              </div>
                            )}
                            <button
                              onClick={() => handleRemoveScreenshot(i)}
                              className="absolute top-1 right-1 bg-white rounded-full p-1 shadow w-6 h-6 flex items-center justify-center "
                            >
                              <span className="text-gray-700 text-sm">×</span>
                            </button>
                          </div>
                          <div
                            className={`text-xs mt-1 truncate w-24  ${
                              isOversized
                                ? "text-red-600 font-medium"
                                : "text-gray-600"
                            }`}
                          >
                            {formatFileSize(file.size)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {validationError && (
                  <p className="text-sm text-red-600">{validationError}</p>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t">
            {apiError && (
              <p className="text-red-500 text-sm mb-1">⚠️ {apiError}</p>
            )}
            <button
              onClick={handleSubmit}
              disabled={hasOversizedFiles || hasOverlappingError}
              className={`w-full py-3 font-medium rounded-lg text-sm ${
                deliveryType === "normal"
                  ? hasOversizedFiles || hasOverlappingError
                    ? "bg-gray-400 cursor-not-allowed text-white"
                    : "bg-blue-500 hover:bg-blue-600 text-white"
                  : "bg-green-500 hover:bg-green-600 text-white"
              }`}
            >
              {deliveryType === "normal"
                ? hasOversizedFiles
                  ? "Remove oversized files to submit"
                  : hasOverlappingError
                    ? "Pre-Approval Already Exists"
                    : "Pre-Approved Now!"
                : "OK"}
            </button>
          </div>
        </div>
      </div>

      {/* COMMENTED OUT: Add New Brand Modal */}
      {/* {showAddBrandModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 z-[60] flex items-center justify-center p-4">
          <div
            className="bg-white rounded-xl shadow-lg w-full max-w-xs sm:max-w-sm mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b flex justify-between items-center">
              <h2 className="text-lg font-semibold">Add New Brand</h2>
              <button
                onClick={handleCloseAddBrandModal}
                className="text-gray-500 hover:text-gray-700"
              >
                <span className="text-xl">×</span>
              </button>
            </div>

            <div className="px-4 py-4 space-y-4">
              <div>
                <span className="text-sm font-medium mb-1 block">
                  Brand Name
                </span>
                <input
                  type="text"
                  value={newBrandName}
                  onChange={(e) => {
                    setNewBrandName(e.target.value);
                    setBrandAddError("");
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="Enter brand name"
                />
              </div>

              <div>
                <span className="text-sm font-medium mb-1 block">
                  Brand Logo (Optional)
                </span>
                <div className="flex items-center gap-3">
                  {newBrandLogoPreview ? (
                    <div className="relative">
                      <img
                        src={newBrandLogoPreview}
                        alt="Brand logo preview"
                        className="w-16 h-16 object-cover rounded-lg border"
                      />
                      <button
                        onClick={() => {
                          setNewBrandLogo(null);
                          setNewBrandLogoPreview(null);
                        }}
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => brandLogoRef.current.click()}
                      className="w-16 h-16 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center hover:border-gray-400"
                    >
                      <span className="text-gray-400 text-2xl">+</span>
                    </button>
                  )}
                  <div>
                    <button
                      onClick={() => brandLogoRef.current.click()}
                      className="text-sm text-blue-600 hover:text-blue-700"
                    >
                      {newBrandLogoPreview ? "Change Logo" : "Upload Logo"}
                    </button>
                    <input
                      type="file"
                      accept="image/*"
                      ref={brandLogoRef}
                      className="hidden"
                      onChange={handleBrandLogoUpload}
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Supported formats: JPG, PNG, etc.
                </p>
              </div>
            </div>

            <div className="px-4 py-3 border-t flex justify-end gap-3">
              <button
                onClick={handleCloseAddBrandModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
                disabled={isAddingBrand}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitNewBrand}
                className="px-4 py-2 text-sm font-medium bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
                disabled={isAddingBrand}
              >
                {isAddingBrand ? "Adding..." : "Add Brand"}
              </button>
            </div>
          </div>
        </div>
      )} */}
    </>
  );
}
